/**
 * F-138 × F-27: the link card's CLIP RANGE — the stretch of the linked
 * video this card stands for — set from the selection toolbar. A popover
 * with a start and an end, typed the way people write times ("1:23",
 * "83", "1m23s"; @kola/shared video-links.ts), saved as milliseconds on
 * the card's payload (`clipStartMs`/`clipEndMs`) through the session's
 * undoable write.
 *
 * Offered on EVERY link card, not only YouTube ones: a Vimeo or a Drive
 * link can carry a range just as usefully, and the storyboard export
 * writes whatever is set. The `t=` a YouTube URL already carries is shown
 * as the placeholder start, so a writer who pasted "at 1:30" sees that
 * the export will read it and can make it explicit with one Enter.
 */
import { clipRangeOf, formatClipTime, parseClipTime, youtubeStartMs, type ClipRange } from "@kola/shared";
import { useState } from "react";

import { registerToolbarAction, type ToolbarActionContext } from "../../toolbar-actions/index.js";
import { showToast } from "../../../ui/toast.js";

function selectedLinkCard(ctx: ToolbarActionContext) {
  const sel = ctx.selection;
  if (sel.kind !== "element") return null;
  const element = ctx.session.board.getElement(sel.elementId);
  if (element === undefined || element.payload.kind !== "linkCard") return null;
  return { id: element.id, payload: element.payload };
}

/** What the inputs would save, or the reason they cannot. */
export function parseClipInputs(start: string, end: string): { range: ClipRange | undefined } | { error: string } {
  const startMs = start.trim().length === 0 ? undefined : parseClipTime(start);
  const endMs = end.trim().length === 0 ? undefined : parseClipTime(end);
  if (start.trim().length > 0 && startMs === undefined) return { error: "Start should look like 1:23" };
  if (end.trim().length > 0 && endMs === undefined) return { error: "End should look like 1:41" };
  if (startMs !== undefined && endMs !== undefined && endMs <= startMs) {
    return { error: "End must come after start" };
  }
  if (startMs === undefined && endMs === undefined) return { range: undefined };
  const range: ClipRange = {};
  if (startMs !== undefined) range.startMs = startMs;
  if (endMs !== undefined) range.endMs = endMs;
  return { range };
}

const iconClip = (
  <svg
    width={16}
    height={16}
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.5}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    {/* A film strip with a bracketed stretch. */}
    <rect x="1.5" y="4" width="13" height="8" rx="1" />
    <path d="M5 4v8M11 4v8" />
    <path d="M6.5 6.5h3M6.5 9.5h3" />
  </svg>
);

function ClipRangePopover({ ctx }: { ctx: ToolbarActionContext }) {
  const card = selectedLinkCard(ctx);
  const current = card === null ? undefined : clipRangeOf(card.payload);
  const urlStart = card === null ? undefined : youtubeStartMs(card.payload.url);
  const [start, setStart] = useState(current?.startMs === undefined ? "" : formatClipTime(current.startMs));
  const [end, setEnd] = useState(current?.endMs === undefined ? "" : formatClipTime(current.endMs));
  const [error, setError] = useState<string | null>(null);

  if (card === null) {
    return (
      <div className="clip-range-popover">
        <p className="clip-range-hint">The card is gone.</p>
      </div>
    );
  }

  const save = (range: ClipRange | undefined) => {
    const written = ctx.session.setLinkCardClip(card.id, range);
    if (!written) {
      showToast("The board can't be edited right now");
      return;
    }
    ctx.closePopover();
  };

  const submit = () => {
    const parsed = parseClipInputs(start, end);
    if ("error" in parsed) {
      setError(parsed.error);
      return;
    }
    save(parsed.range);
  };

  return (
    <form
      className="clip-range-popover"
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
    >
      <p className="clip-range-title">Clip range</p>
      <p className="clip-range-hint">The part of this video the storyboard means.</p>
      <div className="clip-range-fields">
        <label className="clip-range-field">
          <span>Start</span>
          <input
            type="text"
            inputMode="numeric"
            value={start}
            placeholder={urlStart === undefined ? "0:00" : formatClipTime(urlStart)}
            autoFocus
            onChange={(event) => {
              setStart(event.target.value);
              setError(null);
            }}
          />
        </label>
        <span className="clip-range-arrow" aria-hidden="true">
          →
        </span>
        <label className="clip-range-field">
          <span>End</span>
          <input
            type="text"
            inputMode="numeric"
            value={end}
            placeholder="1:41"
            onChange={(event) => {
              setEnd(event.target.value);
              setError(null);
            }}
          />
        </label>
      </div>
      {error !== null && (
        <p className="clip-range-error" role="alert">
          {error}
        </p>
      )}
      <div className="clip-range-actions">
        {current !== undefined && (
          <button type="button" className="btn" onClick={() => save(undefined)}>
            Clear
          </button>
        )}
        <button type="submit" className="btn btn-primary">
          Save
        </button>
      </div>
    </form>
  );
}

registerToolbarAction({
  id: "link-card-clip",
  appliesTo: "elements",
  tooltip: (ctx) => {
    const card = selectedLinkCard(ctx);
    const range = card === null ? undefined : clipRangeOf(card.payload);
    return range === undefined ? "Set clip range" : "Clip range";
  },
  // Right after "Open in browser" (5): the two things one does with a card.
  order: 6,
  icon: iconClip,
  visible: (ctx) => selectedLinkCard(ctx) !== null,
  popover: (ctx) => <ClipRangePopover ctx={ctx} />,
});
